import { useState } from 'react';
import { MessageSquare, Zap, Globe, Shield, ArrowRight, Check, Clock, TrendingUp, Users, Database, Moon, Sun, Languages } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import T from "@/utils/i18n";

interface Feature {
  icon: LucideIcon;
  color: 'blue' | 'cyan';
  title: string;
  desc: string;
}

export default function Home() {
  const [isDark, setIsDark] = useState(T.product.isDark);
  const defaultLang = (T.product.defaultUiLanguage === 'ar' || T.product.defaultUiLanguage === 'en') ? T.product.defaultUiLanguage : 'en' as const;
  const [language, setLanguage] = useState<'ar' | 'en'>(defaultLang); // 'ar' for Arabic, 'en' for English

  const toggleTheme = () => setIsDark(!isDark);
  const toggleLanguage = () => setLanguage(language === 'ar' ? 'en' : 'ar');

  const isRTL = language === 'ar';

  // Translation Object from i18n file
  const t =  T;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark
        ? 'bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white'
        : 'bg-gradient-to-br from-blue-50 via-white to-cyan-50 text-gray-900'
    }`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Navigation */}
      <nav className={`fixed w-full backdrop-blur-md z-50 border-b transition-colors duration-300 ${
        isDark
          ? 'bg-slate-900/80 border-blue-500/20'
          : 'bg-white/90 border-blue-200 shadow-sm'
      }`}>
        <div className=" max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="#" className= "z-60">
              <div className={`flex items-center z-60 space-x-2`}>
                  <MessageSquare className={`w-8 h-8 mx-2 ${isRTL ? 'rotate-y-180' : ''} ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className={`text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent`}>
                    {t.product.name[language]}
                  </span>
              </div>
            </a>
            <div className={`hidden md:block`}>
              <div className={`absolute z-50 top-0 left-0 w-full h-full flex justify-center items-center  space-x-12`}>
              <a href="#how-it-works" className={`transition font-medium ${
                isDark ? 'text-gray-300 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
              }`}>{t.nav.howItWorks[language]}</a>
              <a href="#features" className={`transition font-medium ${
                isDark ? 'text-gray-300 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
              }`}>{t.nav.features[language]}</a>
              <a href="#pricing" className={`transition font-medium ${
                isDark ? 'text-gray-300 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
              }`}>{t.nav.pricing[language]}</a>
            </div>
            </div>
            <div className={`flex items-center space-x-4 z-60`}>
              <a href="/dashboard">
                <button className={`px-6 py-2 rounded-lg font-semibold transition ${
                  isDark
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:shadow-lg hover:shadow-blue-500/50'
                    : 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:shadow-lg hover:shadow-blue-500/30'
                }`}>
                  {t.nav.dashboard[language]}
                </button>
              </a>
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  isDark
                    ? 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'
                    : 'bg-blue-100 hover:bg-blue-200 text-blue-600'
                }`}
                aria-label="Toggle language"
              >
                <Languages className="w-5 h-5" />
              </button>
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-all duration-300 ${
                  isDark
                    ? 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'
                    : 'bg-blue-100 hover:bg-blue-200 text-blue-600'
                }`}
                aria-label="Toggle theme"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div className={`block md:hidden`}>
            <div className={`p-2 top-0 left-0 w-full h-full flex justify-center items-center  space-x-12`}>
              <a href="#features" className={`transition font-medium ${
                isDark ? 'text-gray-300 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
              }`}>{t.nav.features[language]}</a>
              <a href="#pricing" className={`transition font-medium ${
                isDark ? 'text-gray-300 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
              }`}>{t.nav.pricing[language]}</a>
              <a href="#how-it-works" className={`transition font-medium ${
                isDark ? 'text-gray-300 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
              }`}>{t.nav.howItWorks[language]}</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" id="">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className={`inline-flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 rounded-full px-4 py-2 mb-8 transition-colors duration-300 ${
              isDark
                ? 'bg-blue-500/10 border border-blue-500/20'
                : 'bg-blue-100 border border-blue-300'
            }`}>
              <Zap className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <span className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                {t.hero.badge[language]}
              </span>
            </div>

            <h1 className={`text-5xl md:text-7xl font-bold mb-6 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t.hero.title1[language]}
              <span className="block bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
                {t.hero.title2[language]}
              </span>
            </h1>

            <p className={`text-xl md:text-2xl mb-8 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {t.hero.subtitle1[language]}
              <span className="block mt-2">{t.hero.subtitle2[language]}</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button className={`px-8 py-4 rounded-lg font-semibold text-lg transition transform hover:scale-105 flex items-center justify-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 ${
                isDark
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:shadow-xl hover:shadow-blue-500/50'
                  : 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:shadow-xl hover:shadow-blue-500/30'
              }`}>
                <span>{t.hero.startTrial[language]}</span>
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
              <button className={`px-8 py-4 rounded-lg font-semibold text-lg transition ${
                isDark
                  ? 'border-2 border-blue-400/50 hover:bg-blue-500/10'
                  : 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
              }`}>
                {t.hero.watchDemo[language]}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className={`text-3xl font-bold mb-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{t.hero.stat1Value[language]}</div>
                <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t.hero.stat1Label[language]}</div>
              </div>
              <div className="text-center">
                <div className={`text-3xl font-bold mb-1 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>{t.hero.stat2Value[language]}</div>
                <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t.hero.stat2Label[language]}</div>
              </div>
              <div className="text-center">
                <div className={`text-3xl font-bold mb-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{t.hero.stat3Value[language]}</div>
                <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t.hero.stat3Label[language]}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className={`py-20 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark ? 'bg-slate-800/50' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t.problem.title[language]}
            </h2>
            <p className={`text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t.problem.subtitle[language]}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className={`rounded-xl p-8 transition-all duration-300 ${
              isDark
                ? 'bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20'
                : 'bg-gradient-to-br from-red-50 to-red-100/50 border-2 border-red-200 hover:shadow-lg'
            }`}>
              <div className="text-4xl mb-4">❌</div>
              <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t.problem.issue1Title[language]}
              </h3>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                {t.problem.issue1Desc[language]}
              </p>
            </div>

            <div className={`rounded-xl p-8 transition-all duration-300 ${
              isDark
                ? 'bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20'
                : 'bg-gradient-to-br from-orange-50 to-orange-100/50 border-2 border-orange-200 hover:shadow-lg'
            }`}>
              <div className="text-4xl mb-4">🔍</div>
              <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t.problem.issue2Title[language]}
              </h3>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                {t.problem.issue2Desc[language]}
              </p>
            </div>

            <div className={`rounded-xl p-8 transition-all duration-300 ${
              isDark
                ? 'bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20'
                : 'bg-gradient-to-br from-yellow-50 to-yellow-100/50 border-2 border-yellow-200 hover:shadow-lg'
            }`}>
              <div className="text-4xl mb-4">⏰</div>
              <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t.problem.issue3Title[language]}
              </h3>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                {t.problem.issue3Desc[language]}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className={`py-20 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark ? '' : 'bg-gradient-to-br from-blue-50 to-cyan-50'
      }`} id="how-it-works">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t.solution.titlePart1[language]}{t.product.name[language]}{t.solution.titlePart2[language]}
            </h2>
            <p className={`text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t.solution.subtitle[language]}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className={`bg-gradient-to-br from-blue-500 to-cyan-500 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg ${
                isDark ? 'shadow-blue-500/50' : 'shadow-blue-500/30'
              }`}>
                <Database className="w-8 h-8 text-white" />
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.solution.step1Title[language]}</h3>
              <p className={`leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.solution.step1Desc[language]}
              </p>
            </div>

            <div className="text-center">
              <div className={`bg-gradient-to-br from-cyan-500 to-blue-500 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg ${
                isDark ? 'shadow-cyan-500/50' : 'shadow-cyan-500/30'
              }`}>
                <MessageSquare className="w-8 h-8 text-white" />
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.solution.step2Title[language]}</h3>
              <p className={`leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.solution.step2Desc[language]}
              </p>
            </div>

            <div className="text-center">
              <div className={`bg-gradient-to-br from-blue-500 to-cyan-500 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg ${
                isDark ? 'shadow-blue-500/50' : 'shadow-blue-500/30'
              }`}>
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.solution.step3Title[language]}</h3>
              <p className={`leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.solution.step3Desc[language]}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={`py-20 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark ? 'bg-slate-800/50' : 'bg-white'
      }`} id="features">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t.features.title[language]}
            </h2>
            <p className={`text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t.features.subtitle[language]}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {([
              { icon: Globe, color: 'blue', title: t.features.feature1Title[language], desc: t.features.feature1Desc[language] },
              { icon: MessageSquare, color: 'cyan', title: t.features.feature2Title[language], desc: t.features.feature2Desc[language] },
              { icon: Shield, color: 'blue', title: t.features.feature3Title[language], desc: t.features.feature3Desc[language] },
              { icon: Clock, color: 'cyan', title: t.features.feature4Title[language], desc: t.features.feature4Desc[language] },
              { icon: TrendingUp, color: 'blue', title: t.features.feature5Title[language], desc: t.features.feature5Desc[language] },
              { icon: Users, color: 'cyan', title: t.features.feature6Title[language], desc: t.features.feature6Desc[language] }
            ] as Feature[]).map((feature, idx) => (
              <div key={idx} className={`rounded-xl p-6 transition-all duration-300 ${
                isDark
                  ? 'bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 hover:border-blue-500/50'
                  : `bg-gradient-to-br from-${feature.color}-50 to-white border-2 border-${feature.color}-200 hover:shadow-xl hover:border-${feature.color}-400`
              }`}>
                <feature.icon className={`w-10 h-10 mb-4 ${
                  isDark
                    ? `text-${feature.color}-400`
                    : `text-${feature.color}-600`
                }`} />
                <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {feature.title}
                </h3>
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={`py-20 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark ? '' : 'bg-gradient-to-br from-blue-50 to-cyan-50'
      }`} id="pricing">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t.pricing.title[language]}
            </h2>
            <p className={`text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t.pricing.subtitle[language]}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Starter */}
            <div className={`rounded-2xl p-8 transition-all duration-300 ${
              isDark
                ? 'bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 hover:border-blue-500/50'
                : 'bg-white border-2 border-gray-200 hover:border-blue-400 hover:shadow-xl'
            }`}>
              <div className="text-center mb-6">
                <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.pricing.starter[language]}</h3>
                <div className="text-4xl font-bold mb-2">
                  <span className={isDark ? 'text-blue-400' : 'text-blue-600'}>50</span>
                  <span className={`text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}> {t.pricing.perMonth[language]}</span>
                </div>
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t.pricing.starterDesc[language]}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {[`500 ${t.pricing.queries[language]}`, `5 GB ${t.pricing.storage[language]}`, `5 ${t.pricing.users[language]}`, t.pricing.webWidget[language]].map((item, idx) => (
                  <li key={idx} className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
                    <Check className={`w-5 h-5 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'} flex-shrink-0`} />
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{item}</span>
                  </li>
                ))}
              </ul>

              <button className={`w-full px-6 py-3 rounded-lg font-semibold transition ${
                isDark
                  ? 'border-2 border-blue-500 hover:bg-blue-500/10'
                  : 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
              }`}>
                {t.pricing.startTrial[language]}
              </button>
            </div>

            {/* Professional */}
            <div className={`bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl p-8 shadow-2xl relative transform scale-105 ${
              isDark ? 'shadow-blue-500/50 border-2 border-blue-400' : 'shadow-blue-500/30 border-2 border-blue-500'
            }`}>
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className={`bg-gradient-to-r from-yellow-400 to-orange-400 px-4 py-1 rounded-full text-sm font-bold shadow-lg ${
                  isDark ? 'text-slate-900' : 'text-gray-900'
                }`}>
                  {t.pricing.mostPopular[language]}
                </span>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2 text-white">{t.pricing.professional[language]}</h3>
                <div className="text-4xl font-bold mb-2 text-white">
                  <span>85</span>
                  <span className="text-xl"> {t.pricing.perMonth[language]}</span>
                </div>
                <p className="text-blue-100">{t.pricing.professionalDesc[language]}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {[`2,000 ${t.pricing.queries[language]}`, t.pricing.whatsappWeb[language], t.pricing.advancedAnalytics[language]].map((item, idx) => (
                  <li key={idx} className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
                    <Check className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                    <span className="text-white">{item}</span>
                  </li>
                ))}
              </ul>

              <button className="w-full bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition shadow-lg">
                {t.pricing.startTrial[language]}
              </button>
            </div>

            {/* Business */}
            <div className={`rounded-2xl p-8 transition-all duration-300 ${
              isDark
                ? 'bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 hover:border-blue-500/50'
                : 'bg-white border-2 border-gray-200 hover:border-blue-400 hover:shadow-xl'
            }`}>
              <div className="text-center mb-6">
                <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.pricing.business[language]}</h3>
                <div className="text-4xl font-bold mb-2">
                  <span className={isDark ? 'text-blue-400' : 'text-blue-600'}>140</span>
                  <span className={`text-xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}> {t.pricing.perMonth[language]}</span>
                </div>
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t.pricing.businessDesc[language]}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {[`5,000 ${t.pricing.queries[language]}`, t.pricing.unlimitedUsers[language], t.pricing.prioritySupport[language]].map((item, idx) => (
                  <li key={idx} className={`flex items-start ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
                    <Check className={`w-5 h-5 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'} flex-shrink-0`} />
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{item}</span>
                  </li>
                ))}
              </ul>

              <button className={`w-full px-6 py-3 rounded-lg font-semibold transition ${
                isDark
                  ? 'border-2 border-blue-500 hover:bg-blue-500/10'
                  : 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
              }`}>
                {t.pricing.startTrial[language]}
              </button>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className={`mb-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>
              {t.pricing.setupFee[language]} <span className={isDark ? 'text-blue-400' : 'text-blue-600'}>200 {language === 'ar' ? 'د.ك' : 'KWD'}</span> {t.pricing.waived[language]}
            </p>
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
              {t.pricing.trialInfo[language]}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            {t.cta.title[language]}
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            {t.cta.subtitle[language]}
          </p>
          <button className={`bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition transform hover:scale-105 inline-flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 shadow-xl`}>
            <span>{t.cta.button[language]}</span>
            <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
          </button>
          <p className="text-sm text-blue-100 mt-6">{t.cta.info[language]}</p>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-12 px-4 sm:px-6 lg:px-8 border-t transition-colors duration-300 ${
        isDark
          ? 'bg-slate-900 border-blue-500/20'
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto text-center">
          <div className={`flex items-center justify-center space-x-2 mb-4`}>
            <MessageSquare className={`w-6 h-6 mx-1  ${isRTL ? 'rotate-y-180' : ''} ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.product.name[language]}</span>
          </div>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.footer.tagline[language]}
          </p>
          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            {t.footer.copyrightYear[language]}{t.product.name[language]}{t.footer.copyright[language]}
          </p>
        </div>
      </footer>
    </div>
  );
}
